import { useState } from 'react';

export interface FAQ {
  question: string;
  answer: string;
}

export interface FAQContent {
  eyebrow: string;
  headline: string;
  faqs: FAQ[];
}

interface Props {
  content: FAQContent;
}

const FAQSection = ({ content }: Props) => {
  const { eyebrow, headline, faqs } = content;
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section className="bg-white py-36 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px border-t border-surface-border" />

      <div className="relative z-10 max-w-[1280px] mx-auto px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">

          {/* Left sticky panel */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-px w-10 bg-teal" />
                <span className="text-teal text-[11px] tracking-[0.3em] uppercase font-sans font-semibold">
                  {eyebrow}
                </span>
              </div>
              <h2 className="font-serif text-charcoal font-bold leading-[1.1] mb-5"
                style={{ fontSize: 'clamp(2rem, 3.2vw, 2.6rem)' }}>
                {headline}
              </h2>
              <div className="h-[3px] w-12 bg-teal mb-7 rounded-full" />
              <p className="text-charcoal/45 text-[14px] font-sans leading-relaxed mb-12">
                Have questions about drain cleaning, sewer repair, or our service process? Find fast, accurate answers below.
              </p>

              {/* Call card */}
              <div className="rounded-2xl p-6 overflow-hidden relative border border-surface-border bg-surface-soft">
                <div className="relative z-10">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl mb-5"
                    style={{ background: 'rgba(26,74,82,0.08)', border: '1px solid rgba(26,74,82,0.15)' }}>
                    <i className="ri-phone-line text-teal text-base" />
                  </div>
                  <p className="text-charcoal/45 text-[11px] font-sans uppercase tracking-wide mb-1.5">Still have questions?</p>
                  <a href="tel:+19090000000"
                    className="font-serif text-charcoal font-semibold text-lg hover:text-teal transition-colors cursor-pointer">
                    Call Us Anytime
                  </a>
                  <div className="mt-4 pt-4 border-t border-surface-border">
                    <div className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 bg-teal rounded-full" />
                      <span className="text-charcoal/40 text-[11px] font-sans">24/7 Emergency Line</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right accordion */}
          <div className="lg:col-span-8">
            <div className="divide-y divide-surface-border">
              {faqs.map((faq, idx) => (
                <div key={idx} className="group">
                  <button
                    className="w-full text-left py-7 flex items-start justify-between gap-6 cursor-pointer"
                    onClick={() => toggle(idx)}
                  >
                    <div className="flex items-start gap-4">
                      <span className="font-serif text-teal/40 font-bold text-sm mt-0.5 flex-shrink-0 w-8 group-hover:text-teal/70 transition-colors">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                      <span className={`font-sans font-medium text-[15px] leading-snug transition-colors duration-200 ${openIndex === idx ? 'text-charcoal' : 'text-charcoal/60 group-hover:text-charcoal'}`}>
                        {faq.question}
                      </span>
                    </div>
                    <div className={`w-9 h-9 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-300 ${openIndex === idx ? 'bg-teal text-white scale-100' : 'border border-surface-border text-charcoal/25 group-hover:border-teal/35 group-hover:text-teal/60'}`}>
                      <i className={`text-sm transition-transform duration-300 ${openIndex === idx ? 'ri-subtract-line' : 'ri-add-line'}`} />
                    </div>
                  </button>
                  <div className={`overflow-hidden transition-all duration-400 ease-in-out ${openIndex === idx ? 'max-h-72 pb-8' : 'max-h-0'}`}>
                    <p className="text-charcoal/55 text-[15px] leading-[1.9] font-sans pl-12">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
